import { useState } from "react";
import { Button, DatePicker, Input, Popover, Space, Table } from "antd";
import {
  SyncOutlined,
  FileExcelOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { useMSCDAccounts } from "./useMSCDAccounts";
import { useCanEdit } from "@/hooks/usePermission";
import { useDebouncedCallback } from "use-debounce";
import type { Moment } from "moment";
import type { RangeValue } from "rc-picker/lib/interface";

const { Search } = Input;
const { RangePicker } = DatePicker;

export const MSCDAccountWidget = () => {
  const {
    data,
    loading,
    page,
    limit,
    search,
    setPage,
    setLimit,
    setSearch,
    syncMutation,
    exportMutation,
    dispatchMutation,
  } = useMSCDAccounts();
  const canEdit = useCanEdit("mscd-accounts");

  const [syncPopoverOpen, setSyncPopoverOpen] = useState(false);
  const [dateRange, setDateRange] = useState<RangeValue<Moment>>(null);
  const [dispatchPopoverOpen, setDispatchPopoverOpen] = useState(false);
  const [dispatchStartDate, setDispatchStartDate] = useState<Moment | null>(
    null,
  );

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setSearch(value || undefined);
    setPage(0);
  }, 500);

  const handleSync = () => {
    const beginDate = dateRange?.[0]?.format("YYYY-MM-DD");
    const endDate = dateRange?.[1]?.format("YYYY-MM-DD");
    syncMutation.mutate({ beginDate, endDate });
    setSyncPopoverOpen(false);
    setDateRange(null);
  };

  const syncContent = (
    <Space direction="vertical" size="middle">
      <RangePicker
        value={dateRange}
        onChange={(values) => setDateRange(values)}
        placeholder={["Эхлэх огноо", "Дуусах огноо"]}
      />
      <Space>
        <Button onClick={() => setSyncPopoverOpen(false)}>Цуцлах</Button>
        <Button
          type="primary"
          onClick={handleSync}
          loading={syncMutation.isLoading}
        >
          Синхрон
        </Button>
      </Space>
    </Space>
  );

  const handleDispatch = () => {
    const startDate = dispatchStartDate?.valueOf();
    dispatchMutation.mutate({ startDate });
    setDispatchPopoverOpen(false);
    setDispatchStartDate(null);
  };

  const dispatchContent = (
    <Space direction="vertical" size="middle">
      <DatePicker
        value={dispatchStartDate}
        onChange={(value) => setDispatchStartDate(value)}
        placeholder="Эхлэх огноо (заавал биш)"
      />
      <Space>
        <Button onClick={() => setDispatchPopoverOpen(false)}>Цуцлах</Button>
        <Button
          type="primary"
          onClick={handleDispatch}
          loading={dispatchMutation.isLoading}
        >
          Илгээх
        </Button>
      </Space>
    </Space>
  );

  return (
    <>
      <div className="page-header">
        <h3>Үнэт цаасны данс</h3>
        <Space>
          <Search
            placeholder="Хайх..."
            defaultValue={search}
            onChange={(e) => debouncedSearch(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
          {canEdit && (
            <>
              <Button
                icon={<FileExcelOutlined />}
                loading={exportMutation.isLoading}
                onClick={() => exportMutation.mutate()}
              >
                Excel татах
              </Button>
              <Popover
                content={syncContent}
                title="Синхрончлох"
                trigger="click"
                open={syncPopoverOpen}
                onOpenChange={setSyncPopoverOpen}
              >
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  loading={syncMutation.isLoading}
                >
                  Синхрон
                </Button>
              </Popover>
              <Popover
                content={dispatchContent}
                title="Данс үүсгэх хүсэлт илгээх"
                trigger="click"
                open={dispatchPopoverOpen}
                onOpenChange={setDispatchPopoverOpen}
              >
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={dispatchMutation.isLoading}
                >
                  Batch илгээх
                </Button>
              </Popover>
            </>
          )}
        </Space>
      </div>
      <div className="page-body">
        <div className="page-table">
          <Table
            scroll={{ x: 1200 }}
            dataSource={data?.accounts ?? []}
            size="small"
            loading={loading || syncMutation.isLoading}
            rowKey="_id"
            bordered
            pagination={{
              current: page + 1,
              pageSize: limit,
              total: (data?.totalPages ?? 0) * limit,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              onChange: (newPage, pageSize) => {
                setPage(newPage - 1);
                setLimit(pageSize);
              },
            }}
            columns={[
              {
                title: "Дансны дугаар",
                dataIndex: "accountNumber",
                key: "accountNumber",
              },
              {
                title: "BDC Account ID",
                dataIndex: "bdcAccountId",
                key: "bdcAccountId",
              },
              {
                title: "Овог",
                dataIndex: "lastName",
                key: "lastName",
              },
              {
                title: "Нэр",
                dataIndex: "firstName",
                key: "firstName",
              },
              {
                title: "Регистрийн дугаар",
                dataIndex: "registryNumber",
                key: "registryNumber",
              },
              {
                title: "Харилцагчийн төрөл",
                dataIndex: "customerType",
                key: "customerType",
              },
              {
                title: "Төрөл",
                dataIndex: "type",
                key: "type",
              },
              {
                title: "Үүсгэсэн огноо",
                dataIndex: "createdDate",
                key: "createdDate",
              },
            ]}
          />
        </div>
      </div>
    </>
  );
};
